import React, { useState } from "react";
import axios from '../../axiosInstance';

type TaxFormData = {
  address: string;
  city: string;
  state: string;
  zip: string;
  materialTotal: number;
  installTotal: number;
  freightTotal: number;
  fmTotal: number;
  offloadTotal: number;
  shelfTotal: number;
  trashTotal: number;
  discount: number;
  additionalCharge: number;
  storageTotal: number;
  renovations: boolean;
  conversion: boolean;
  /** Materials are nursing home furniture: backend taxes them under PH403010. */
  nursingHomeFurniture: boolean;
};

type TaxResult = {
  totalTax: number;
  materialTax: number;
  installTax: number;
  freightTax: number;
  fmTax: number;
  offloadTax: number;
  shelfTax: number;
  storageTax: number;
};

const TaxDashboard: React.FC = () => {
  const [form, setForm] = useState<TaxFormData>({
    address: "",
    city: "",
    state: "",
    zip: "",
    materialTotal: 0,
    installTotal: 0,
    freightTotal: 0,
    fmTotal: 0,
    offloadTotal: 0,
    shelfTotal: 0,
    trashTotal: 0,
    discount: 0,
    additionalCharge: 0,
    storageTotal: 0,
    renovations: false,
    conversion: false,
    nursingHomeFurniture: false
  });

  const [result, setResult] = useState<TaxResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [materialDisplay, setMaterialDisplay] = useState("0");
  const [installDisplay, setInstallDisplay] = useState("0");
  const [freightDisplay, setFreightDisplay] = useState("0");
  const [fmDisplay, setFmDisplay] = useState("0");
  const [offloadDisplay, setOffloadDisplay] = useState("0");
  const [shelfDisplay, setShelfDisplay] = useState("0");
  const [trashDisplay, setTrashDisplay] = useState("0");
  const [discountDisplay, setDiscountDisplay] = useState("0");
  const [chargeDisplay, setChargeDisplay] = useState("0");
  const [storageDisplay, setStorageDisplay] = useState("0");
  const formatCurrency = (value: number) =>
    value.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  
  const parseCurrency = (value: string) =>
    parseFloat(value.replace(/[^0-9.-]+/g, "")) || 0;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : type === "number" ? parseFloat(value) : value,
    }));
  };

  const handleExclusiveCheckbox = (name: "renovations" | "conversion") => {
    setForm((prev) => {
      const newValue = !prev[name]; 
  
      return {
        ...prev,
        renovations: name === "renovations" ? newValue : false,
        conversion: name === "conversion" ? newValue : false,
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);

    if (!form.address || !form.city || !form.state || !form.zip) {
      setError("Please fill in the full address.");
      return;
    }

    setIsLoading(true);

    // Parse the displayed values directly so the request always matches what
    // the user sees on screen. The form state only updates on blur, so
    // submitting with Enter (no blur) would otherwise send stale amounts.
    const amounts = {
      materialTotal: parseCurrency(materialDisplay),
      installTotal: parseCurrency(installDisplay),
      freightTotal: parseCurrency(freightDisplay),
      fmTotal: parseCurrency(fmDisplay),
      offloadTotal: parseCurrency(offloadDisplay),
      shelfTotal: parseCurrency(shelfDisplay),
      trashTotal: parseCurrency(trashDisplay),
      storageTotal: parseCurrency(storageDisplay),
      discount: parseCurrency(discountDisplay),
      additionalCharge: parseCurrency(chargeDisplay),
    };
    setForm((prev) => ({ ...prev, ...amounts }));

    try {
      const response = await axios.post("/quotes/mf/totalTax", {
          address: form.address,
          city: form.city,
          state: form.state,
          zip: form.zip,
          materialTotal: amounts.materialTotal,
          installTotal: amounts.installTotal,
          freightTotal: amounts.freightTotal,
          fmTotal: amounts.fmTotal,
          offloadTotal: amounts.offloadTotal,
          shelfTotal: amounts.shelfTotal,
          trashTotal: amounts.trashTotal,
          storageTotal: amounts.storageTotal,
          discount: amounts.discount,
          additionalCharge: amounts.additionalCharge,
          renovations: form.renovations,
          conversion: form.conversion,
          nursingHomeFurniture: form.nursingHomeFurniture
      });


      const data: TaxResult = response.data;
      setResult(data);
    } catch (err) {
      console.error(err);
      setError(
        err instanceof Error ? err.message : "Unexpected error while calculating tax."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        maxWidth: "900px",
        margin: "0 auto",
        padding: "2rem",
        fontFamily: "system-ui",
      }}
    >
      <h1 style={{ fontSize: "1.75rem", fontWeight: 600, marginBottom: "1.5rem" }}>
        Tax Calculator Dashboard
      </h1>

      <form
        onSubmit={handleSubmit}
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "1rem",
          padding: "1.5rem",
          borderRadius: "1rem",
          boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
          background: "#ffffff",
        }}
      >
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={{ display: "block", fontSize: ".875rem", marginBottom: ".25rem" }}>
            Street Address
          </label>
          <input
            type="text"
            name="address"
            value={form.address}
            onChange={handleChange}
            required
            style={{
              width: "100%",
              padding: ".6rem .75rem",
              borderRadius: ".5rem",
              border: "1px solid #d1d5db",
            }}
          />
        </div>

        <div>
          <label style={{ display: "block", fontSize: ".875rem", marginBottom: ".25rem" }}>
            City
          </label>
          <input
            type="text"
            name="city"
            value={form.city}
            onChange={handleChange}
            required
            style={{
              width: "100%",
              padding: ".6rem .75rem",
              borderRadius: ".5rem",
              border: "1px solid #d1d5db",
            }}
          />
        </div>

        <div>
          <label style={{ display: "block", fontSize: ".875rem", marginBottom: ".25rem" }}>
            State
          </label>
          <input
            type="text"
            name="state"
            value={form.state}
            onChange={handleChange}
            required
            maxLength={2}
            style={{
              width: "100%",
              padding: ".6rem .75rem",
              borderRadius: ".5rem",
              border: "1px solid #d1d5db",
              textTransform: "uppercase",
            }}
          />
        </div>

        <div>
          <label style={{ display: "block", fontSize: ".875rem", marginBottom: ".25rem" }}>
            ZIP Code
          </label>
          <input
            type="text"
            name="zip"
            value={form.zip}
            onChange={handleChange}
            required
            style={{
              width: "100%",
              padding: ".6rem .75rem",
              borderRadius: ".5rem",
              border: "1px solid #d1d5db",
            }}
          />
        </div>

        <div>
          <label style={{ display: "block", fontSize: ".875rem", marginBottom: ".25rem" }}>
            Product Total
          </label>
          <input
            type="text"
            name="materialTotal"
            value={materialDisplay}
            onChange={(e) => {
              setMaterialDisplay(e.target.value);
            }}
            onBlur={() => {
              const parsed = parseCurrency(materialDisplay);
              setForm((prev) => ({
                ...prev,
                materialTotal: parsed,
              }));
              setMaterialDisplay(formatCurrency(parsed));
            }}
            onFocus={() => {
              setMaterialDisplay(form.materialTotal.toString());
            }}
            style={{
              width: "100%",
              padding: ".6rem .75rem",
              borderRadius: ".5rem",
              border: "1px solid #d1d5db",
            }}
          />
        </div>

        <div>
          <label style={{ display: "block", fontSize: ".875rem", marginBottom: ".25rem" }}>
            Install Total
          </label>
          <input
            type="text"
            name="installTotal"
            value={installDisplay}
            onChange={(e) => {
              setInstallDisplay(e.target.value);
            }}
            onBlur={() => {
              const parsed = parseCurrency(installDisplay);
              setForm((prev) => ({
                ...prev,
                installTotal: parsed,
              }));
              setInstallDisplay(formatCurrency(parsed));
            }}
            onFocus={() => {
              setInstallDisplay(form.installTotal.toString());
            }}
            style={{
              width: "100%",
              padding: ".6rem .75rem",
              borderRadius: ".5rem",
              border: "1px solid #d1d5db",
            }}
          />
        </div>

        <div>
          <label style={{ display: "block", fontSize: ".875rem", marginBottom: ".25rem" }}>
            Freight Total
          </label>
          <input
            type="text"
            name="freightTotal"
            value={freightDisplay}
            onChange={(e) => {
              setFreightDisplay(e.target.value);
            }}
            onBlur={() => {
              const parsed = parseCurrency(freightDisplay);
              setForm((prev) => ({
                ...prev,
                freightTotal: parsed,
              }));
              setFreightDisplay(formatCurrency(parsed));
            }}
            onFocus={() => {
              setFreightDisplay(form.freightTotal.toString());
            }}
            style={{
              width: "100%",
              padding: ".6rem .75rem",
              borderRadius: ".5rem",
              border: "1px solid #d1d5db",
            }}
          />
        </div>
        <div style={{ alignSelf: "end" }}>
          <label style={{ display: "block", fontSize: ".875rem", marginBottom: ".25rem" }}>
            FM Total
          </label>
          <input
            type="text"
            name="fmTotal"
            value={fmDisplay}
            onChange={(e) => {
              setFmDisplay(e.target.value);
            }}
            onBlur={() => {
              const parsed = parseCurrency(fmDisplay);
              setForm((prev) => ({
                ...prev,
                fmTotal: parsed,
              }));
              setFmDisplay(formatCurrency(parsed));
            }}
            onFocus={() => {
              setFmDisplay(form.fmTotal.toString());
            }}
            style={{
              width: "100%",
              padding: ".6rem .75rem",
              borderRadius: ".5rem",
              border: "1px solid #d1d5db",
            }}
          />
        </div>
        <div style={{ alignSelf: "end" }}>
          <label style={{ display: "block", fontSize: ".875rem", marginBottom: ".25rem" }}>
            Offload Equipment Total
          </label>
          <input
            type="text"
            name="offloadTotal"
            value={offloadDisplay}
            onChange={(e) => {
              setOffloadDisplay(e.target.value);
            }}
            onBlur={() => {
              const parsed = parseCurrency(offloadDisplay);
              setForm((prev) => ({
                ...prev,
                offloadTotal: parsed,
              }));
              setOffloadDisplay(formatCurrency(parsed));
            }}
            onFocus={() => {
              setOffloadDisplay(form.offloadTotal.toString());
            }}
            style={{
              width: "100%",
              padding: ".6rem .75rem",
              borderRadius: ".5rem",
              border: "1px solid #d1d5db",
            }}
          />
        </div>
        <div>
          <label style={{ display: "block", fontSize: ".875rem", marginBottom: ".25rem" }}>
            Shelving Removal Total (Shelf removal is 75*unit total. If patch and paint, 200*unit total)
          </label>
          <input
            type="text"
            name="shelfTotal"
            value={shelfDisplay}
            onChange={(e) => {
              setShelfDisplay(e.target.value);
            }}
            onBlur={() => {
              const parsed = parseCurrency(shelfDisplay);
              setForm((prev) => ({
                ...prev,
                shelfTotal: parsed,
              }));
              setShelfDisplay(formatCurrency(parsed));
            }}
            onFocus={() => {
              setShelfDisplay(form.shelfTotal.toString());
            }}
            style={{
              width: "100%",
              padding: ".6rem .75rem",
              borderRadius: ".5rem",
              border: "1px solid #d1d5db",
            }}
          />
        </div>
        <div>
          <label style={{ display: "block", fontSize: ".875rem", marginBottom: ".25rem" }}>
            Trash Removal Total
          </label>
          <input
            type="text"
            name="trashTotal"
            value={trashDisplay}
            onChange={(e) => {
              setTrashDisplay(e.target.value);
            }}
            onBlur={() => {
              const parsed = parseCurrency(trashDisplay);
              setForm((prev) => ({
                ...prev,
                trashTotal: parsed,
              }));
              setTrashDisplay(formatCurrency(parsed));
            }}
            onFocus={() => {
              setTrashDisplay(form.trashTotal.toString());
            }}
            style={{
              width: "100%",
              padding: ".6rem .75rem",
              borderRadius: ".5rem",
              border: "1px solid #d1d5db",
            }}
          />
        </div>
        <div>
          <label style={{ display: "block", fontSize: ".875rem", marginBottom: ".25rem" }}>
            Discount?
          </label>
          <input
            type="text"
            name="discount"
            value={discountDisplay}
            onChange={(e) => {
              setDiscountDisplay(e.target.value);
            }}
            onBlur={() => {
              const parsed = parseCurrency(discountDisplay);
              setForm((prev) => ({
                ...prev,
                discount: parsed,
              }));
              setDiscountDisplay(formatCurrency(parsed));
            }}
            onFocus={() => {
              setDiscountDisplay(form.discount.toString());
            }}
            style={{
              width: "100%",
              padding: ".6rem .75rem",
              borderRadius: ".5rem",
              border: "1px solid #d1d5db",
            }}
          />
        </div>
        <div>
          <label style={{ display: "block", fontSize: ".875rem", marginBottom: ".25rem" }}>
            Additional Charge?
          </label>
          <input
            type="text"
            name="additionalCharge"
            value={chargeDisplay}
            onChange={(e) => {
              setChargeDisplay(e.target.value);
            }}
            onBlur={() => {
              const parsed = parseCurrency(chargeDisplay);
              setForm((prev) => ({
                ...prev,
                additionalCharge: parsed,
              }));
              setChargeDisplay(formatCurrency(parsed));
            }}
            onFocus={() => {
              setChargeDisplay(form.additionalCharge.toString());
            }}
            style={{
              width: "100%",
              padding: ".6rem .75rem",
              borderRadius: ".5rem",
              border: "1px solid #d1d5db",
            }}
          />
        </div>
        <div>
          <label style={{ display: "block", fontSize: ".875rem", marginBottom: ".25rem" }}>
            Storage Fee?
          </label>
          <input
            type="text"
            name="storageTotal"
            value={storageDisplay}
            onChange={(e) => {
              setStorageDisplay(e.target.value);
            }}
            onBlur={() => {
              const parsed = parseCurrency(storageDisplay);
              setForm((prev) => ({
                ...prev,
                storageTotal: parsed,
              }));
              setStorageDisplay(formatCurrency(parsed));
            }}
            onFocus={() => {
              setStorageDisplay(form.storageTotal.toString());
            }}
            style={{
              width: "100%",
              padding: ".6rem .75rem",
              borderRadius: ".5rem",
              border: "1px solid #d1d5db",
            }}
          />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={{ fontSize: ".875rem" }}>Renovations?</label>
          <div style={{ marginTop: ".5rem" }}>
            <input
              type="checkbox"
              name="renovations"
              checked={form.renovations}
              onChange={() => handleExclusiveCheckbox("renovations")}
              style={{ width: "18px", height: "18px", marginRight: "8px" }}
            />
            <span>Yes, this is renovations</span>
          </div>
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={{ fontSize: ".875rem" }}>Conversion?</label>
          <div style={{ marginTop: ".5rem" }}>
            <input
              type="checkbox"
              name="conversion"
              checked={form.conversion}
              onChange={() => handleExclusiveCheckbox("conversion")}
              style={{ width: "18px", height: "18px", marginRight: "8px" }}
            />
            <span>Yes, this is a conversion</span>
          </div>
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={{ fontSize: ".875rem" }}>Nursing Home Furniture?</label>
          <div style={{ marginTop: ".5rem" }}>
            <input
              type="checkbox"
              name="nursingHomeFurniture"
              checked={form.nursingHomeFurniture}
              onChange={handleChange}
              style={{ width: "18px", height: "18px", marginRight: "8px" }}
            />
            <span>Yes, the materials are nursing home furniture (taxed under PH403010)</span>
          </div>
        </div>
        <div style={{ gridColumn: "1 / -1", textAlign: "right" }}>
          <button
            type="submit"
            disabled={isLoading}
            style={{
              padding: ".7rem 1.5rem",
              borderRadius: "999px",
              border: "none",
              fontWeight: 600,
              cursor: "pointer",
              background: "#111827",
              color: "#ffffff",
              opacity: isLoading ? 0.7 : 1,
            }}
          >
            {isLoading ? "Calculating..." : "Submit"}
          </button>
        </div>
      </form>

      {/* Error */}
      {error && (
        <div
          style={{
            marginTop: "1rem",
            padding: ".75rem 1rem",
            borderRadius: ".75rem",
            background: "#fef2f2",
            color: "#b91c1c",
            fontSize: ".9rem",
          }}
        >
          {error}
        </div>
      )}

      {/* Result */}
      {result && (
        <div
          style={{
            marginTop: "1.5rem",
            padding: "1.5rem",
            borderRadius: "1rem",
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
          }}
        >
          <h2 style={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: ".75rem" }}>
            Tax Result
          </h2>
          <p>
            <strong>Tax on Material:</strong>{" "}
            {formatCurrency(result.materialTax)}
          </p>
          <p>
            <strong>Tax on Install:</strong>{" "}
            {formatCurrency(result.installTax)}
          </p>
          <p>
            <strong>Tax on Freight:</strong>{" "}
            {formatCurrency(result.freightTax)}
          </p>
          <p>
            <strong>Tax on FM Charge:</strong>{" "}
            {formatCurrency(result.fmTax)}
          </p>
          <p>
            <strong>Tax on Offload Equipment:</strong>{" "}
            {formatCurrency(result.offloadTax)}
          </p>
          <p>
            <strong>Tax on Shelving Removal:</strong>{" "}
            {formatCurrency(result.shelfTax)}
          </p>
          <p>
            <strong>Tax on Storage Fee:</strong>{" "}
            {formatCurrency(result.storageTax)}
          </p>
          <p>
            <strong>Total Tax:</strong>{" "}
            {formatCurrency(result.totalTax)}
          </p>
        </div>
      )}
    </div>
  );
};

export default TaxDashboard;
